import AnimateInView from '@atoms/AnimateInView/AnimateInView';
import Button from '@atoms/Button/Button';
import Text from '@atoms/Text/Text';
import SiteEnum from '@models/enums/SiteEnum';
import { IWishlistCarousel } from '@models/IWishlistCarousel';
import EditWishlist from '@molecules/EditWishlist/EditWishlist';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import WishlistCardSwitch from '@molecules/WishlistCardSwitch/WishlistCardSwitch';
import WishlistEmptyState from '@molecules/WishlistEmptyState/WishlistEmptyState';
import { fadeInUp } from '@utility/config/animations';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props extends IWishlistCarousel {
  readonly onShare: () => void;
  readonly refreshImageUrlEndpoint: string;
}

const WishlistCarousel = ({
  emptyStateSubText,
  emptyStateTitle,
  items,
  labels,
  onShare,
  refreshImageUrlEndpoint,
  shareLabel,
  title,
  wishlistId,
}: Props) => {
  const titleElement = useMemo(
    () => (
      <div className="tw-w-full">
        <Text className="tw-py-xs tw-text-grey-100" variant="headline">
          {title}
        </Text>
        <div className="tw-flex tw-w-full tw-flex-grow tw-gap-4 md:tw-w-auto">
          <div className="tw-w-1/2 md:tw-w-auto">
            <EditWishlist {...labels} title={title} wishlistId={wishlistId} />
          </div>
          <Button
            label={shareLabel}
            variant="grey80"
            modifier="outline"
            onClick={onShare}
            iconName="share"
            buttonClasses="tw-w-1/2 md:tw-w-auto"
          />
        </div>
      </div>
    ),
    [title],
  );

  if (!items.length)
    return (
      <div className="tw-grid-container tw-gap-y-sm md:tw-gap-y-md xxl:tw-gap-y-[60px]">
        <div className="tw-col-span-full tw-flex tw-items-center">
          {titleElement}
        </div>
        <div className="tw-col-span-full">
          <WishlistEmptyState
            title={emptyStateTitle}
            subText={emptyStateSubText}
            isPrimary={false}
          />
        </div>
      </div>
    );

  return (
    <AnimateInView className="tw-bg-grey-10" desktopOffset={0.2}>
      <NavigationCarousel
        site={SiteEnum.Deco}
        id={title}
        numberOfSlides={items.length}
        slidesToShowDesktop={3}
        slidesToShowTabletPortrait={2}
        slidesToShowMobile={1.1}
        titleElement={titleElement}
      >
        {items.map((item, index) => (
          <SwiperSlide
            key={`slide-${index}`}
            className="stretch-card tw-h-full tw-max-w-[400px]"
          >
            <motion.div variants={fadeInUp} className="tw-h-full">
              <WishlistCardSwitch
                refreshImageUrlEndpoint={refreshImageUrlEndpoint}
                item={item}
                wishlistId={wishlistId}
                isListing={false}
              />
            </motion.div>
          </SwiperSlide>
        ))}
      </NavigationCarousel>
    </AnimateInView>
  );
};
export default WishlistCarousel;
